import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Phone, Loader2 } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "bm_chat_session";
const MSG_STORAGE_KEY = "bm_chat_msgs";

function getOrCreateSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      // 12-char random session id, well within backend's 8–64 range.
      sid = "bmc-" + Math.random().toString(36).slice(2, 12);
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "bmc-" + Math.random().toString(36).slice(2, 12);
  }
}

// Monotonic id for React keys — no collisions with restored history since we
// never persist the id, and every mount starts at 0.
let _msgIdCounter = 0;
const nextMsgId = () => `m-${++_msgIdCounter}-${Date.now()}`;

const OPENING_MSG = {
  id: "opening",
  role: "assistant",
  content:
    "Hi! I'm the BM Towing assistant. Ask me about 24/7 towing, flatbed, roadside, jump starts, lockouts, tire changes, or accident recovery in Bellevue, Kirkland, and the Eastside.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([OPENING_MSG]);
  const listRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Restore session + history on first open (lazy — don't hit server unless needed).
  useEffect(() => {
    sessionIdRef.current = getOrCreateSessionId();
    try {
      const cached = localStorage.getItem(MSG_STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) {
          // Backfill stable ids for transcripts saved before this field existed
          // so React keys stay unique after a refresh.
          setMessages(parsed.map((m) => (m.id ? m : { ...m, id: nextMsgId() })));
        }
      }
    } catch (err) {
      // localStorage can throw in private mode / on quota errors. Not fatal.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("ChatWidget: failed to restore cached transcript:", err);
      }
    }
  }, []);

  // Persist visible transcript so the widget survives a refresh.
  useEffect(() => {
    try {
      localStorage.setItem(MSG_STORAGE_KEY, JSON.stringify(messages));
    } catch (err) {
      // Quota exceeded / private mode — chat still works in-memory.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("ChatWidget: failed to persist transcript:", err);
      }
    }
  }, [messages]);

  // Auto-scroll to bottom on new tokens.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);

    setMessages((m) => [
      ...m,
      { id: nextMsgId(), role: "user", content: text },
      { id: nextMsgId(), role: "assistant", content: "", pending: true },
    ]);

    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // Read the SSE stream and append token deltas onto the trailing assistant message.
      // Each SSE event is "data: <chunk>\n\n". Content newlines are escaped as \n.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const evt = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (!evt.startsWith("data:")) continue;
          const chunk = evt.slice(5).replace(/^\s/, "");
          if (chunk === "[done]") continue;
          if (chunk === "[error]") throw new Error("stream error");
          const decoded = chunk.replace(/\\n/g, "\n");
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant") {
              copy[copy.length - 1] = {
                ...last,
                pending: false,
                content: (last.content || "") + decoded,
              };
            }
            return copy;
          });
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") {
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              "Sorry — I couldn't reach the assistant. Please call " +
              PHONE_DISPLAY +
              " and a real person will help right now.",
            pending: false,
          };
        }
        return copy;
      });
    } finally {
      setSending(false);
    }
  }, [input, sending]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Launcher button — sits above the mobile call bar on small screens */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-widget-launcher"
          aria-label="Open BM Towing chat assistant"
          className="fixed z-50 right-4 lg:bottom-6 bottom-[92px] bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full p-3.5 border-2 border-black shadow-[0_8px_24px_rgba(250,204,21,0.35)] transition-all"
        >
          <MessageCircle className="h-6 w-6" strokeWidth={2.5} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          role="dialog"
          aria-label="BM Towing chat assistant"
          className="fixed z-50 right-4 lg:bottom-6 bottom-[92px] w-[min(360px,calc(100vw-2rem))] max-h-[70vh] flex flex-col bg-slate-950 border-2 border-yellow-400 shadow-[0_16px_48px_rgba(0,0,0,0.6)] rounded-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-yellow-400 text-slate-900 border-b-2 border-black">
            <div className="flex items-center gap-2 font-display font-black uppercase tracking-wider text-sm">
              <MessageCircle className="h-4 w-4" strokeWidth={3} />
              BM Towing Assistant
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close"
              aria-label="Close chat"
              className="p-1 border-2 border-transparent hover:bg-slate-900 hover:text-yellow-400 hover:border-black"
            >
              <X className="h-4 w-4" strokeWidth={3} />
            </button>
          </div>

          {/* Message list */}
          <div
            ref={listRef}
            data-testid="chat-widget-messages"
            className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-950"
            style={{ minHeight: "220px" }}
          >
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`chat-msg-${m.role}-${m.id}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap px-3 py-2 ${
                    m.role === "user"
                      ? "bg-yellow-400 text-slate-900 font-medium"
                      : "bg-slate-900 text-slate-100 border border-slate-700"
                  }`}
                >
                  {m.content}
                  {m.pending && (
                    <Loader2 className="inline-block h-3 w-3 ml-1 animate-spin text-yellow-400" aria-hidden="true" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Quick call CTA — always visible so we never gate emergencies behind chat */}
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="chat-widget-call"
            className="flex items-center justify-center gap-2 bg-slate-900 hover:bg-black text-yellow-400 border-t-2 border-yellow-400 py-2.5 font-display font-black uppercase tracking-wider text-xs"
          >
            <Phone className="h-3.5 w-3.5" strokeWidth={3} /> Or call {PHONE_DISPLAY}
          </a>

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            className="flex gap-2 p-2 bg-slate-950 border-t border-slate-800"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              data-testid="chat-widget-input"
              placeholder="Ask about towing, roadside, lockouts…"
              rows={1}
              maxLength={2000}
              disabled={sending}
              aria-label="Type your message"
              className="flex-1 resize-none bg-slate-900 border border-slate-700 focus:border-yellow-400 outline-none px-3 py-2 text-sm text-white placeholder:text-slate-500 disabled:opacity-60"
              style={{ maxHeight: "80px" }}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chat-widget-send"
              aria-label="Send message"
              className="bg-yellow-400 hover:bg-yellow-500 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 border-2 border-black px-3 flex items-center justify-center"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
              )}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
